import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'src/utils/axiosCatalog';
import { REHYDRATE } from 'redux-persist';

// Region by Employee
export const getRegionByEmployee = createAsyncThunk(
  'manualDiscount/getRegionByEmployee',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/GetRegionByEmployee/${employeeId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch regions');
    }
  }
);

// Get Active Payment Types
export const getActivePaymentTypes = createAsyncThunk(
  'manualDiscount/getActivePaymentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/GetActivePaymentTypes');
      
      // Response string olarak JSON gelebilir, parse et
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('JSON parse error:', e);
          return [];
        }
      }
      
      // Eğer data array değilse, array'e çevir veya boş array döndür
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // Object ise array'e çevir
        return Object.values(data);
      }
      
      return [];
    } catch (error) {
      console.error('Payment Types API Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active payment types');
    }
  }
);

// Get Active Warrantors
export const getActiveWarrantors = createAsyncThunk(
  'manualDiscount/getActiveWarrantors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/getActiveWarrantors');
      
      // Response string olarak JSON gelebilir, parse et
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('JSON parse error:', e);
          return [];
        }
      }
      
      // Eğer data array değilse, array'e çevir veya boş array döndür
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // Object ise array'e çevir
        return Object.values(data);
      }
      
      return [];
    } catch (error) {
      console.error('Warrantors API Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active warrantors');
    }
  }
);

// Get Scheduling Service Areas
export const getSchedulingServiceAreas = createAsyncThunk(
  'manualDiscount/getSchedulingServiceAreas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/GetSchedulingServiceAreas');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch service areas');
    }
  }
);

// Manual Discount Search
export const searchManualDiscount = createAsyncThunk(
  'manualDiscount/searchManualDiscount',
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await axios.post('/GetDiscountManualSearch', searchParams);
      
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('JSON parse error:', e);
        }
      }
      
      if (Array.isArray(data)) {
        return data.map(item => ({
          region: item.Region,
          workCaseId: item.WorkCaseId,
          workOrder: item.WorkOrder,
          serviceShop: item.ServiceShop,
          retailer: item.Retailer,
          warrantyType: item.WarrantyType,
          status: item.Status,
          openDate: item.OpenDate,
          repairDate: item.RepairDate,
          labourTotal: item.LabourTotal,
          spareTotal: item.SpareTotal,
          transportTotal: item.TransportTotal,
          extraTotal: item.ExtraTotal,
          total: item.Total,
          km: item.KM,
          invoiceDate: item.InvoiceDate,
          sku: item.SKU,
          skuDescription: item.SKUDescription,
          contactId: item.ContactId,
          contractorEarnings: item.ContractorEarnings,
          // Payment fields - Backend field name'lerine uygun
          warrantor: item.Warrantor,
          paymentTypeId: item.PaymentTypeId,
          paymentTypeDetail: item.PaymentTypeDetail
        }));
      }
      
      return data || [];
    } catch (error) {
      console.error('Search error:', error);
      return rejectWithValue(error.response?.data?.message || 'Search failed');
    }
  }
);

// Get Current Totals
export const getCurrentTotals = createAsyncThunk(
  'manualDiscount/getCurrentTotals',
  async ({ workOrderId, tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/GetCurrentTotals/${workOrderId}/${tenantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch current totals');
    }
  }
);

// Apply Manual Discount
export const applyManualDiscount = createAsyncThunk(
  'manualDiscount/applyManualDiscount',
  async (params, { rejectWithValue }) => {
    try {
      // Backend'deki DTO property name'lerine uygun olarak gönder
      const payload = {
        WorkOrderId: params.workOrderId,
        NewLabourTotal: params.newLabourTotal,
        NewSparePartTotal: params.newSparePartTotal,
        NewTransportTotal: params.newTransportTotal,
        NewExtraTotal: params.newExtraTotal,
        EmployeeId: params.employeeId,
        // Payment information - Backend DTO'ya uygun
        Warrantor: params.warrantor ? parseInt(params.warrantor) : null,
        PaymentTypeId: params.paymentTypeId ? parseInt(params.paymentTypeId) : null,
        PaymentTypeDetail: params.paymentTypeDetail || null
      };
      
      console.log('Applying discount with payload:', payload);
      
      const response = await axios.post('/ApplyManualDiscount', payload);
      return response.data;
    } catch (error) {
      console.error('Apply discount error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to apply discount';
      return rejectWithValue(errorMessage);
    }
  }
);

const manualDiscountSlice = createSlice({
  name: 'manualDiscount',
  initialState: {
    loading: false,
    error: '',
    regionList: [],
    selectedRegion: null,
    searchResults: [],
    startDate: null,
    endDate: null,
    serviceAreas: [],
    currentTotals: null,
    applyDiscountResult: null,
    applyingDiscount: false,
    // Payment lists
    paymentTypeList: [],
    warrantorList: []
  },
  reducers: {
    setSelectedRegion(state, action) {
      state.selectedRegion = action.payload;
    },
    setStartDate(state, action) {
      state.startDate = action.payload;
    },
    setEndDate(state, action) {
      state.endDate = action.payload;
    },
    clearSearchResults(state) {
      state.searchResults = [];
    },
    clearCurrentTotals(state) {
      state.currentTotals = null;
    },
    clearApplyDiscountResult(state) {
      state.applyDiscountResult = null;
    },
    clearError(state) {
      state.error = '';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state, action) => {
        if (action.payload?.manualDiscount) {
          const payload = action.payload.manualDiscount;
          state.selectedRegion = payload.selectedRegion;
        }
      })
      
      // Get Region by Employee
      .addCase(getRegionByEmployee.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getRegionByEmployee.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
      })
      .addCase(getRegionByEmployee.fulfilled, (state, action) => {
        state.regionList = action.payload;
        state.loading = false;
        state.error = '';
      })
      
      // Get Active Payment Types
      .addCase(getActivePaymentTypes.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getActivePaymentTypes.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
        state.paymentTypeList = []; // Hata durumunda boş array
      })
      .addCase(getActivePaymentTypes.fulfilled, (state, action) => {
        // GÜVENLE: Array olduğundan emin ol
        state.paymentTypeList = Array.isArray(action.payload) ? action.payload : [];
        state.loading = false;
        state.error = '';
      })
      
      // Get Active Warrantors
      .addCase(getActiveWarrantors.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getActiveWarrantors.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
        state.warrantorList = []; // Hata durumunda boş array
      })
      .addCase(getActiveWarrantors.fulfilled, (state, action) => {
        // GÜVENLE: Array olduğundan emin ol
        state.warrantorList = Array.isArray(action.payload) ? action.payload : [];
        state.loading = false;
        state.error = '';
      })
      
      // Get Scheduling Service Areas
      .addCase(getSchedulingServiceAreas.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getSchedulingServiceAreas.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
      })
      .addCase(getSchedulingServiceAreas.fulfilled, (state, action) => {
        state.serviceAreas = action.payload;
        state.loading = false;
        state.error = '';
      })
      
      // Search Manual Discount
      .addCase(searchManualDiscount.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(searchManualDiscount.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
        state.searchResults = [];
      })
      .addCase(searchManualDiscount.fulfilled, (state, action) => {
        state.searchResults = action.payload;
        state.loading = false;
        state.error = '';
      })
      
      // Get Current Totals
      .addCase(getCurrentTotals.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getCurrentTotals.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.loading = false;
      })
      .addCase(getCurrentTotals.fulfilled, (state, action) => {
        state.currentTotals = action.payload;
        state.loading = false;
        state.error = '';
      })
      
      // Apply Manual Discount
      .addCase(applyManualDiscount.pending, (state) => {
        state.applyingDiscount = true;
        state.error = '';
      })
      .addCase(applyManualDiscount.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
        state.applyingDiscount = false;
      })
      .addCase(applyManualDiscount.fulfilled, (state, action) => {
        state.applyDiscountResult = action.payload;
        state.applyingDiscount = false;
        state.error = '';
      });
  }
});

export const {
  setSelectedRegion,
  setStartDate,
  setEndDate,
  clearSearchResults,
  clearCurrentTotals,
  clearApplyDiscountResult,
  clearError
} = manualDiscountSlice.actions;

export default manualDiscountSlice;